import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { commitBulkImport, downloadBulkImportTemplate, validateBulkImport } from '../../services/BulkImportService';
import { hasStoredPermission } from '../../utils/dashboardPermissions';

const canUseBulkImport = () => hasStoredPermission('SUPER_ADMIN') || hasStoredPermission('BULK_IMPORT_DATA');

const BulkImportButton = ({ type, label, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);

  const visibleRows = useMemo(() => validation?.rows?.filter((row) => row.status === 'error').slice(0, 20) || [], [validation]);

  if (!canUseBulkImport()) return null;

  const reset = () => {
    setFile(null);
    setValidation(null);
    setResult(null);
    setError('');
    setValidating(false);
    setImporting(false);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const handleDownload = async () => {
    setError('');
    try {
      await downloadBulkImportTemplate(type);
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo descargar el template');
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    setValidating(true);
    setError('');
    setResult(null);
    try {
      const response = await validateBulkImport(type, file);
      setValidation(response);
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo validar el archivo');
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!file || !validation?.valid) return;
    setImporting(true);
    setError('');
    try {
      const response = await commitBulkImport(type, file);
      setResult(response);
      setValidation(response.validation);
      if (response.inserted > 0) onSuccess?.();
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo importar el archivo');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setOpen(true)}>
        Importar archivo
      </Button>
      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>Importar {label}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Descargar template
            </Button>
            <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
              Seleccionar archivo
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(event) => {
                  setFile(event.target.files?.[0] || null);
                  setValidation(null);
                  setResult(null);
                  setError('');
                }}
              />
            </Button>
            {file && <Typography variant="body2">{file.name}</Typography>}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {result && <Alert severity="success" sx={{ mb: 2 }}>{result.inserted} filas importadas</Alert>}
          {validation && (
            <Alert severity={validation.valid ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {validation.valid
                ? `${validation.validRows} filas listas para importar`
                : `${validation.errorRows} filas con errores de ${validation.totalRows}`}
            </Alert>
          )}

          {visibleRows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fila</TableCell>
                  <TableCell>Errores</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={row.row}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.errors.join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cerrar</Button>
          <LoadingButton onClick={handleValidate} loading={validating} disabled={!file || importing}>
            Validar
          </LoadingButton>
          <LoadingButton variant="contained" onClick={handleCommit} loading={importing} disabled={!validation?.valid || validating || Boolean(result)}>
            Importar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkImportButton;
